import { Avatar, Box, Button, Stack, SvgIcon, Typography } from '@mui/material';
import { IActivity, getFileSource } from 'API/ActivitiesLobby/activitiesLobby';
import { getSupplierRedirectCode } from 'API/Treatment/treatment';
import { Loader } from 'Components';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { ACTIVITY_TYPE } from 'Enum/ActivitiesLobby';
import { useIsDesktop } from 'Hooks';
import { PurpleOutlineWarningIcon } from 'Images';
import { ReactComponent as PurpleDownloadIcon } from 'Images/General/PurpleDownloadIcon.svg';
import { downloadFile } from 'Utils';
import { sendEvent } from 'Utils/sendEvent';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface IActivityItem {
  activity: IActivity;
}

const errors: { [key: number]: string } = {
  1: Dictionary[DICTIONARY.ActivitiesLobby].missingPaycheckError,
  4: Dictionary[DICTIONARY.ActivitiesLobby].missingForm106Error,
  14: Dictionary[DICTIONARY.ActivitiesLobby].missingFlyingCarpetError,
  15: Dictionary[DICTIONARY.ActivitiesLobby].missingEshetToursError,
};
export const flyingCarpetId = 14,
  eshetToursId = 15;

export const ActivityItem = ({ activity }: IActivityItem) => {
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const isDesktop = useIsDesktop();
  const navigate = useNavigate();

  const getErrorMessage = (activityId: number) =>
    errors[activityId] ||
    Dictionary[DICTIONARY.ActivitiesLobby].missingFileError;

  const onActivityClick = async (activityTypeId: ACTIVITY_TYPE) => {
    sendEvent(
      activity?.analyticsData?.componentName || '',
      activity?.analyticsData?.eventName || '',
      activity?.analyticsData?.label
    );
    switch (activityTypeId) {
      case ACTIVITY_TYPE.FILE:
        setError('');
        setIsLoading(true);
        const fileSource = await getFileSource(activity.link);
        if (fileSource) downloadFile(fileSource, '.pdf', activity.label, false);
        else setError(getErrorMessage(activity.id));
        setIsLoading(false);
        break;

      case ACTIVITY_TYPE.INTERNAL:
        navigate(activity.link);
        break;

      case ACTIVITY_TYPE.EXTERNAL:
        if (activity.id === flyingCarpetId || activity.id === eshetToursId) {
          const code = await getSupplierRedirectCode(
            activity.id === flyingCarpetId ? 1 : 2
          );
          window.open(
            `https://${
              activity.id === flyingCarpetId
                ? 'flying-shikum.co.il'
                : 'shikum.eshet.com'
            }/${code ? '?code=' + encodeURIComponent(code) : ''}`,
            '_blank'
          );
        } else window.open(activity.link, '_blank');
        break;
    }
  };

  const showIcon = activity.activityTypeId === ACTIVITY_TYPE.FILE;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <Button
        key={activity.label}
        onClick={() => onActivityClick(activity.activityTypeId)}
        sx={{
          p: isDesktop ? '16px' : '12px',
          border: '1px solid',
          borderColor: 'purple2.shade_300',
          borderRadius: '12px',
          width: isDesktop ? '179px' : '155px',
          height: '100%',
          bgcolor: isDesktop ? undefined : 'white',
          justifyContent: 'space-between',
          ':disabled': {
            color: 'inherit',
            bgcolor: 'primary.shade_100',
            borderColor: 'primary.shade_200',
            '& path': { stroke: '#B7CCFF' },
          },
        }}
        aria-describedby={
          error ? `${activity.label.trim().replace(' ', '-')}-error` : undefined
        }
      >
        <Typography
          color={'initial'}
          variant={isDesktop ? 'body3' : 'body5'}
          textAlign={'left'}
          width={showIcon ? '110px' : undefined}
        >
          {activity.label}
        </Typography>
        {showIcon &&
          (isLoading ? (
            <Box
              position={'relative'}
              sx={{
                top: '0',
                height: '24px ',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Loader size={16} />
            </Box>
          ) : (
            <SvgIcon component={PurpleDownloadIcon} />
          ))}
      </Button>
      {error && (
        <Stack
          flexDirection={'row'}
          gap={'8px'}
          maxWidth={isDesktop ? '179px' : '155px'}
          alignItems='center'
        >
          <Avatar
            src={PurpleOutlineWarningIcon}
            alt=''
            sx={{ width: '15px', height: '15px' }}
          />
          <Typography
            id={`${activity.label.trim().replace(' ', '-')}-error`}
            role='alert'
            variant={'body7'}
          >
            {error}
          </Typography>
        </Stack>
      )}
    </Box>
  );
};
